/** Workspace settings: organization details, members, account. */

import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/api/client';
import { authApi, organizationApi } from '@/api/endpoints';
import { useAuth } from '@/app/auth';
import { Permission } from '@/app/permissions';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardHeader,
  Field,
  LoadingState,
  PageHeader,
  Table,
  Td,
  Th,
} from '@/components/ui';
import {
  formatDate,
  inputClass,
} from '@/utils/format';

const ROLES = ['viewer', 'editor', 'seo_manager', 'admin', 'owner'] as const;

export function SettingsPage() {
  const { user, can } = useAuth();
  const queryClient = useQueryClient();

  const orgQuery = useQuery({
    queryKey: ['organization'],
    queryFn: () => organizationApi.current(),
  });
  const membersQuery = useQuery({
    queryKey: ['members'],
    queryFn: () => organizationApi.members(),
  });

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<string>('viewer');
  const [passwords, setPasswords] = useState({ current_password: '', new_password: '' });

  const inviteMutation = useMutation({
    mutationFn: () => organizationApi.invite({ email: inviteEmail, role: inviteRole }),
    onSuccess: async () => {
      setInviteEmail('');
      await queryClient.invalidateQueries({ queryKey: ['members'] });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (membershipId: string) => organizationApi.removeMember(membershipId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['members'] }),
  });

  const roleMutation = useMutation({
    mutationFn: ({ id, role }: { id: string; role: string }) =>
      organizationApi.updateMember(id, { role }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['members'] }),
  });

  const passwordMutation = useMutation({
    mutationFn: () => authApi.changePassword(passwords),
    onSuccess: () => setPasswords({ current_password: '', new_password: '' }),
  });

  if (orgQuery.isLoading) return <LoadingState />;
  const organization = orgQuery.data;

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <PageHeader title="Settings" description="Workspace, team and account." />

      {organization ? (
        <Card>
          <CardHeader title="Workspace" />
          <dl className="grid gap-3 sm:grid-cols-2">
            <div>
              <dt className="text-xs text-ink-subtle">Name</dt>
              <dd className="text-sm font-medium text-ink">{organization.name}</dd>
            </div>
            <div>
              <dt className="text-xs text-ink-subtle">Plan</dt>
              <dd className="text-sm font-medium text-ink capitalize">{organization.plan_tier}</dd>
            </div>
            <div>
              <dt className="text-xs text-ink-subtle">Websites</dt>
              <dd className="tnum text-sm font-medium text-ink">{organization.site_count}</dd>
            </div>
            <div>
              <dt className="text-xs text-ink-subtle">Members</dt>
              <dd className="tnum text-sm font-medium text-ink">{organization.member_count}</dd>
            </div>
          </dl>
        </Card>
      ) : null}

      <Card>
        <CardHeader
          title="Team"
          description="Roles separate proposing a change from approving it. An editor can create actions; only an SEO manager or above can approve and execute them."
        />

        {can(Permission.ORG_MANAGE_MEMBERS) ? (
          <form
            onSubmit={(event: FormEvent) => {
              event.preventDefault();
              inviteMutation.mutate();
            }}
            className="mb-5 flex flex-wrap items-end gap-3 rounded-md bg-surface-muted p-4"
          >
            <div className="min-w-[14rem] flex-1">
              <Field label="Invite by email" htmlFor="invite-email">
                <input
                  id="invite-email"
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={(event) => setInviteEmail(event.target.value)}
                  className={inputClass}
                  placeholder="colleague@company.com"
                />
              </Field>
            </div>
            <div>
              <Field label="Role" htmlFor="invite-role">
                <select
                  id="invite-role"
                  value={inviteRole}
                  onChange={(event) => setInviteRole(event.target.value)}
                  className={inputClass}
                >
                  {ROLES.map((role) => (
                    <option key={role} value={role}>
                      {role.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <Button type="submit" variant="primary" loading={inviteMutation.isPending}>
              Invite
            </Button>
          </form>
        ) : null}

        {inviteMutation.isError ? (
          <div className="mb-4">
            <Callout tone="danger">
              {inviteMutation.error instanceof ApiError
                ? inviteMutation.error.message
                : 'Invitation failed.'}
            </Callout>
          </div>
        ) : null}

        {membersQuery.data ? (
          <Table>
            <thead>
              <tr>
                <Th>Member</Th>
                <Th>Role</Th>
                <Th>Status</Th>
                <Th>Joined</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {membersQuery.data.map((member) => (
                <tr key={member.id}>
                  <Td>
                    <span className="block text-sm text-ink">
                      {member.full_name ?? member.email}
                    </span>
                    <span className="block text-xs text-ink-subtle">{member.email}</span>
                  </Td>
                  <Td>
                    {can(Permission.ORG_MANAGE_MEMBERS) && member.user_id !== user?.id ? (
                      <select
                        value={member.role}
                        onChange={(event) =>
                          roleMutation.mutate({ id: member.id, role: event.target.value })
                        }
                        aria-label={`Role for ${member.email}`}
                        className="rounded-md border border-border-strong bg-surface px-2 py-1 text-xs capitalize"
                      >
                        {ROLES.map((role) => (
                          <option key={role} value={role}>
                            {role.replace(/_/g, ' ')}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <Badge tone="neutral">{member.role.replace(/_/g, ' ')}</Badge>
                    )}
                  </Td>
                  <Td>
                    <Badge tone={member.status === 'active' ? 'success' : 'warning'}>
                      {member.status}
                    </Badge>
                  </Td>
                  <Td className="text-xs text-ink-subtle">{formatDate(member.accepted_at)}</Td>
                  <Td align="right">
                    {can(Permission.ORG_MANAGE_MEMBERS) && member.user_id !== user?.id ? (
                      <button
                        type="button"
                        onClick={() => removeMutation.mutate(member.id)}
                        className="text-xs text-danger hover:underline"
                      >
                        Remove
                      </button>
                    ) : null}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : null}

        {roleMutation.isError ? (
          <div className="mt-4">
            <Callout tone="danger">
              {roleMutation.error instanceof ApiError
                ? roleMutation.error.message
                : 'Could not change the role.'}
            </Callout>
          </div>
        ) : null}
      </Card>

      <Card>
        <CardHeader title="Change password" description="You will be signed out of all sessions." />
        <form
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            passwordMutation.mutate();
          }}
          className="max-w-sm space-y-4"
        >
          {passwordMutation.isError ? (
            <Callout tone="danger">
              {passwordMutation.error instanceof ApiError
                ? passwordMutation.error.message
                : 'Could not change your password.'}
            </Callout>
          ) : null}
          {passwordMutation.isSuccess ? (
            <Callout tone="success">Password changed. Please sign in again.</Callout>
          ) : null}

          <Field label="Current password" htmlFor="current-password" required>
            <input
              id="current-password"
              type="password"
              required
              autoComplete="current-password"
              value={passwords.current_password}
              onChange={(event) =>
                setPasswords({ ...passwords, current_password: event.target.value })
              }
              className={inputClass}
            />
          </Field>
          <Field label="New password" htmlFor="new-password" hint="At least 10 characters." required>
            <input
              id="new-password"
              type="password"
              required
              minLength={10}
              autoComplete="new-password"
              value={passwords.new_password}
              onChange={(event) => setPasswords({ ...passwords, new_password: event.target.value })}
              className={inputClass}
            />
          </Field>
          <Button type="submit" variant="primary" loading={passwordMutation.isPending}>
            Change password
          </Button>
        </form>
      </Card>
    </div>
  );
}
